/**
 * Export Panel Component
 * Provides PDF/CSV export functionality for various reports
 */
import React, { useState } from 'react';
import { exportAPI } from '../services/api';
import '../styles/ExportPanel.css';

const ExportPanel = ({ projectId = null }) => {
  const [selectedFormat, setSelectedFormat] = useState('csv');
  const [dateRange, setDateRange] = useState({ from: '', to: '' });
  const [exporting, setExporting] = useState(null);

  const reportTypes = [
    { value: 'task_list', label: 'Görev Listesi', icon: '📝' },
    { value: 'project_summary', label: 'Proje Özeti', icon: '📊' },
    { value: 'health_report', label: 'Sağlık Raporu', icon: '💚' },
    { value: 'timesheet_report', label: 'Zaman Çizelgesi', icon: '⏰' },
    { value: 'expense_report', label: 'Gider Raporu', icon: '💰' },
  ];

  const handleExport = async (reportType) => {
    setExporting(reportType);
    try {
      const filters = {
        project_id: projectId,
      };

      if (dateRange.from) {
        filters.date_from = dateRange.from;
      }
      if (dateRange.to) {
        filters.date_to = dateRange.to;
      }

      const response = await exportAPI.generateReport(
        reportType,
        selectedFormat,
        filters
      );

      // Download the file
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      const extension = selectedFormat;
      const timestamp = new Date().toISOString().slice(0, 19).replace(/:/g, '-');
      link.setAttribute('download', `${reportType}_${timestamp}.${extension}`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);

      // Show success message
      showToast('✅ Rapor başarıyla indirildi!', 'success');
    } catch (error) {
      console.error('Export error:', error);
      showToast('❌ Rapor indirme hatası!', 'error');
    } finally {
      setExporting(null);
    }
  };

  const showToast = (message, type) => {
    // Simple toast notification
    const toast = document.createElement('div');
    toast.className = `toast toast-${type}`;
    toast.textContent = message;
    document.body.appendChild(toast);

    setTimeout(() => {
      toast.classList.add('show');
    }, 100);

    setTimeout(() => {
      toast.classList.remove('show');
      setTimeout(() => {
        document.body.removeChild(toast);
      }, 300);
    }, 3000);
  };

  const resetFilters = () => {
    setDateRange({ from: '', to: '' });
    setSelectedFormat('csv');
  };

  return (
    <div className="export-panel">
      <div className="panel-header">
        <h3>
          <span className="icon">📥</span>
          Rapor Dışa Aktarma
        </h3>
        {(dateRange.from || dateRange.to) && (
          <button className="reset-btn" onClick={resetFilters}>
            🔄 Sıfırla
          </button>
        )}
      </div>

      {/* Format Selector */}
      <div className="format-section">
        <label className="section-label">📄 Format</label>
        <div className="format-buttons">
          <button
            className={`format-btn ${selectedFormat === 'csv' ? 'active' : ''}`}
            onClick={() => setSelectedFormat('csv')}
          >
            <span className="format-icon">📊</span>
            <span className="format-name">CSV</span>
            <span className="format-desc">Excel</span>
          </button>

          <button
            className={`format-btn ${selectedFormat === 'pdf' ? 'active' : ''}`}
            onClick={() => setSelectedFormat('pdf')}
          >
            <span className="format-icon">📄</span>
            <span className="format-name">PDF</span>
            <span className="format-desc">Belge</span>
          </button>
        </div>
      </div>

      {/* Date Range */}
      <div className="date-range-section">
        <label className="section-label">📅 Tarih Aralığı (Opsiyonel)</label>
        <div className="date-inputs">
          <input
            type="date"
            value={dateRange.from}
            onChange={(e) => setDateRange({ ...dateRange, from: e.target.value })}
            placeholder="Başlangıç"
          />
          <span className="date-separator">→</span>
          <input
            type="date"
            value={dateRange.to}
            onChange={(e) => setDateRange({ ...dateRange, to: e.target.value })}
            placeholder="Bitiş"
          />
        </div>
      </div>

      {/* Report Types */}
      <div className="reports-section">
        <label className="section-label">📋 Rapor Türü</label>
        <div className="report-buttons">
          {reportTypes.map((report) => (
            <button
              key={report.value}
              className={`report-btn ${exporting === report.value ? 'exporting' : ''}`}
              onClick={() => handleExport(report.value)}
              disabled={exporting !== null}
            >
              <span className="report-icon">{report.icon}</span>
              <span className="report-label">{report.label}</span>
              {exporting === report.value ? (
                <span className="loading-spinner">⏳</span>
              ) : (
                <span className="download-icon">⬇️</span>
              )}
            </button>
          ))}
        </div>
      </div>

      {/* Info Section */}
      <div className="info-section">
        <div className="info-box">
          <span className="info-icon">💡</span>
          <div className="info-content">
            <strong>İpucu:</strong>
            <p>
              CSV dosyaları Excel'de açılabilir. PDF dosyaları metin tabanlıdır.
              {dateRange.from || dateRange.to
                ? ' Tarih aralığı uygulandı.'
                : ' Tüm veriler dahil edilecek.'}
            </p>
          </div>
        </div>
      </div>

      {/* Quick Actions */}
      <div className="quick-actions">
        <h4>⚡ Hızlı İşlemler</h4>
        <div className="action-buttons">
          <button
            className="action-btn"
            onClick={() => {
              const today = new Date().toISOString().split('T')[0];
              const lastWeek = new Date(Date.now() - 7 * 24 * 60 * 60 * 1000)
                .toISOString()
                .split('T')[0];
              setDateRange({ from: lastWeek, to: today });
            }}
          >
            📅 Son 7 Gün
          </button>

          <button
            className="action-btn"
            onClick={() => {
              const today = new Date().toISOString().split('T')[0];
              const lastMonth = new Date(Date.now() - 30 * 24 * 60 * 60 * 1000)
                .toISOString()
                .split('T')[0];
              setDateRange({ from: lastMonth, to: today });
            }}
          >
            📅 Son 30 Gün
          </button>

          <button
            className="action-btn"
            onClick={() => {
              const today = new Date();
              const firstDay = new Date(today.getFullYear(), today.getMonth(), 1)
                .toISOString()
                .split('T')[0];
              const todayStr = today.toISOString().split('T')[0];
              setDateRange({ from: firstDay, to: todayStr });
            }}
          >
            📅 Bu Ay
          </button>
        </div>
      </div>

      {/* Export Statistics */}
      {projectId && (
        <div className="stats-section">
          <p className="project-info">
            <span className="info-icon">📁</span>
            Seçili proje için rapor oluşturulacak
          </p>
        </div>
      )}
    </div>
  );
};

export default ExportPanel;
